"use client";

import { useForm } from "react-hook-form";
import type { Resolver, FieldErrors } from "react-hook-form";
import * as z from "zod";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { useReCaptcha } from "next-recaptcha-v3";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { sendInvestmentInquiryEmail } from "@/actions/email";

const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

type FormData = {
	name: string;
	email: string;
	phone: string;
	companyName: string;
	cui: string;
	message: string;
};

function InvestmentInquiryFormBase({
	getToken,
	criteriaChecked,
}: {
	getToken: (() => Promise<string>) | null;
	criteriaChecked: string[];
}) {
	const t = useTranslations("InvestmentPage");

	const schema = useMemo(
		() =>
			z.object({
				name: z.string().min(2, { message: t("nameMin") }),
				email: z.string().email({ message: t("invalidEmail") }),
				phone: z.string(),
				companyName: z.string().min(2, { message: t("companyMin") }),
				cui: z.string().min(2, { message: t("cuiMin") }),
				message: z.string().min(10, { message: t("messageMin") }),
			}),
		[t]
	);

	const customZodResolver: Resolver<FormData> = useMemo(
		() => (values) => {
			const result = schema.safeParse(values);
			if (result.success) {
				return { values: result.data, errors: {} };
			}
			const errors: FieldErrors<FormData> = {};
			for (const issue of result.error.issues) {
				const path = issue.path[0] as keyof FormData;
				if (path && !errors[path]) {
					errors[path] = { type: "validation", message: issue.message };
				}
			}
			return { values: {} as Record<string, never>, errors };
		},
		[schema]
	);

	const [status, setStatus] = useState<
		"idle" | "submitting" | "success" | "error"
	>("idle");
	const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		reset,
	} = useForm<FormData>({
		resolver: customZodResolver,
		mode: "onChange",
		defaultValues: { phone: "", companyName: "", cui: "", message: "" },
	});

	const onSubmit = async (data: FormData) => {
		setStatus("submitting");
		setRecaptchaError(null);
		let token: string | null = null;
		if (getToken) {
			try {
				token = await getToken();
			} catch {
				setRecaptchaError(t("recaptchaError"));
				setStatus("idle");
				return;
			}
		}
		const formData = new FormData();
		formData.append("name", data.name);
		formData.append("email", data.email);
		formData.append("phone", data.phone);
		formData.append("companyName", data.companyName);
		formData.append("cui", data.cui);
		formData.append("message", data.message);
		formData.append("criteriaChecked", JSON.stringify(criteriaChecked));
		if (token) formData.append("recaptchaToken", token);

		const result = await sendInvestmentInquiryEmail(formData);
		if (result.success) {
			setStatus("success");
			reset();
		} else {
			setStatus("error");
		}
	};

	const inputClass =
		"h-14 sm:h-16 text-[1.6rem] sm:text-[1.8rem] px-6 py-5 placeholder:text-gray-500 dark:placeholder:text-gray-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-gray-100";
	const errorClass = "text-red-500 text-[1.4rem] sm:text-[1.6rem] mt-1";

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="space-y-5 mt-10 pt-10 border-t-2 border-slate-200 dark:border-gray-600"
		>
			<h3 className="text-[2.2rem] font-bold text-slate-900 dark:text-white mb-2">
				{t("formTitle")}
			</h3>
			<p className="text-[1.6rem] text-slate-600 dark:text-gray-300 mb-6">
				{t("formIntro")}
			</p>

			<div>
				<Input
					{...register("name")}
					placeholder={t("namePlaceholder")}
					className={`${inputClass} w-full rounded-lg ${
						errors.name ? "border-red-500" : ""
					}`}
				/>
				{errors.name && <p className={errorClass}>{errors.name.message}</p>}
			</div>
			<div>
				<Input
					{...register("email")}
					placeholder={t("emailPlaceholder")}
					type="email"
					className={`${inputClass} w-full rounded-lg ${
						errors.email ? "border-red-500" : ""
					}`}
				/>
				{errors.email && <p className={errorClass}>{errors.email.message}</p>}
			</div>
			<div>
				<Input
					{...register("phone")}
					placeholder={t("phonePlaceholder")}
					type="tel"
					className={`${inputClass} w-full rounded-lg ${
						errors.phone ? "border-red-500" : ""
					}`}
				/>
			</div>
			<div>
				<Input
					{...register("companyName")}
					placeholder={t("companyPlaceholder")}
					className={`${inputClass} w-full rounded-lg ${
						errors.companyName ? "border-red-500" : ""
					}`}
				/>
				{errors.companyName && (
					<p className={errorClass}>{errors.companyName.message}</p>
				)}
			</div>
			<div>
				<Input
					{...register("cui")}
					placeholder={t("cuiPlaceholder")}
					className={`${inputClass} w-full rounded-lg ${
						errors.cui ? "border-red-500" : ""
					}`}
				/>
				{errors.cui && <p className={errorClass}>{errors.cui.message}</p>}
			</div>
			<div>
				<textarea
					{...register("message")}
					placeholder={t("messagePlaceholder")}
					rows={5}
					className={`flex min-h-[140px] w-full rounded-lg border px-6 py-5 text-[1.6rem] sm:text-[1.8rem] placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 ${
						errors.message ? "border-red-500" : "border-gray-300"
					}`}
				/>
				{errors.message && (
					<p className={errorClass}>{errors.message.message}</p>
				)}
			</div>

			{recaptchaError && (
				<p className={`${errorClass} text-red-500`}>{recaptchaError}</p>
			)}

			<Button
				type="submit"
				size="xl"
				disabled={status === "submitting" || !isValid}
				loading={status === "submitting"}
				className="w-full min-h-[48px] text-[1.05rem] sm:text-[1.8rem] font-medium rounded-lg"
			>
				{status === "submitting" ? t("sending") : t("formButton")}
			</Button>

			{status === "success" && (
				<p className="text-green-600 dark:text-green-400 text-center text-[1.8rem] font-medium">
					{t("formSuccess")}
				</p>
			)}
			{status === "error" && (
				<p className="text-red-600 dark:text-red-400 text-center text-[1.8rem] font-medium">
					{t("formError")}
				</p>
			)}
		</form>
	);
}

function InvestmentInquiryFormWithRecaptcha({
	criteriaChecked,
}: {
	criteriaChecked: string[];
}) {
	const { executeRecaptcha } = useReCaptcha();
	const getToken = () => executeRecaptcha("investment_inquiry_submit");
	return (
		<InvestmentInquiryFormBase
			getToken={getToken}
			criteriaChecked={criteriaChecked}
		/>
	);
}

export default function InvestmentInquiryForm({
	criteriaChecked,
}: {
	criteriaChecked: string[];
}) {
	if (!recaptchaSiteKey) {
		return (
			<InvestmentInquiryFormBase
				getToken={null}
				criteriaChecked={criteriaChecked}
			/>
		);
	}
	return (
		<InvestmentInquiryFormWithRecaptcha criteriaChecked={criteriaChecked} />
	);
}
